// LoginScreen — the signed-out front door (H-A5, Wave A).
//
// Rewritten from the legacy inline-style .jsx onto the H-0 token system
// (cream/terracotta/Fraunces — same language as OnboardingWelcome, the
// authed front door, so the two doors read as one product). Gate-rendered
// from App.jsx inside `.sb-root` (scoped preflight), but every interactive
// element is also explicitly UA-proofed (appearance-none / border-0 /
// explicit bg) so the screen renders identically if it ever mounts
// outside the scope.
//
// UX-DILIGENCE §3: this screen carried the ONE confirmed 44px touch-target
// miss (the CTA had no min-height). Fixed: min-h-[60px], full-width.
//
// Error state is designed + calm (parent-credible): a soft card with a
// plain-language line and the technical detail demoted underneath —
// never a raw exception string as the headline.
//
// MA-289: ROLE-FIRST sequencing. Step 1 (chosenRole === null) shows ONLY
// the role pills — no auth button exists in the DOM until a role is
// tapped. Step 2 shows that role's sign-in option with the choice echoed
// + a way back. This mount ALWAYS starts at step 1 (no pre-seed from a
// persisted role) — the only legal way to skip both steps is the
// App.jsx `if (!user)` gate itself (an already-signed-in user never
// reaches this component at all). Pre-seeding from getUserRole() would
// silently reopen the roleless-Google path for any returning-but-signed-
// out device, which is exactly what this item removes.
//
// MA-289 slice (b): a "Continue with email" option, flag-gated by
// isEmailAuthEnabled (off by default — see src/lib/emailAuthFlag.ts +
// docs/ops/FOUNDER-FYI.md). Magic-link/OTP only — no passwords.
//
// MA-941 (D-TUX-30): with the flag on, the email carries a CODE that is typed
// in this same tab, not a link. A link fails for school families (PKCE: a
// link opened in another browser or app cannot finish; a school mail scanner
// that opens it first spends it), and the pending claim code lives in this
// tab's sessionStorage. After Send, the step shows a digits-only code field
// (6 to 10 digits, whatever length the Supabase project is set to); Sign in
// calls verifyOtp with type "email" (src/lib/auth/emailCode.ts). The role was
// already written at tap time (handleRoleSelect), exactly as the Google path
// keeps it, and the tab never changes, so the pending claim code survives.
// signInWithEmailOtp and src/supabaseClient.js are unchanged. With the flag
// off nothing here renders (the DOM is pinned identical to main by
// src/screens/__tests__/ma941EmailCode.test.mjs).

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Pal } from "@/components/Pal";
import { signInWithGoogle, signInWithEmailOtp, supabase } from "../supabaseClient.js";
import { setUserRole, type UserRole } from "@/lib/userRole";
import { readPendingClaimCode } from "@/lib/teacher/pendingClaimCode";
import {
  TEACHER_SIGN_IN_HEADLINE,
  clearPendingTeacherStart,
  deviceHasFamilyChildren,
  readPendingTeacherStart,
} from "@/lib/teacher/pendingTeacherStart";
import { isEmailAuthEnabled } from "@/lib/emailAuthFlag";
import {
  EMAIL_CODE_ERROR_TEXT,
  EMAIL_CODE_RESEND_AFTER_MS,
  emailCodeDigits,
  isCompleteEmailCode,
  resendWaitLeftMs,
  verifyEmailCode,
  type LastCodeSend,
} from "@/lib/auth/emailCode";

type EmailStep = "closed" | "open" | "sending" | "sent";

/** The four-colour Google "G" — inline so the button reads as the real
 *  OAuth affordance parents recognise. Decorative (aria-hidden). */
function GoogleMark({ size = 20 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full bg-white"
      style={{ width: size + 8, height: size + 8 }}
    >
      <svg width={size} height={size} viewBox="0 0 48 48">
        <path
          fill="#EA4335"
          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
          fill="#4285F4"
          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        />
        <path
          fill="#FBBC05"
          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
          fill="#34A853"
          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
      </svg>
    </span>
  );
}

export default function LoginScreen() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // MA-57: role chooser — shown unconditionally (not gated on device mode).
  // pw_device_mode defaults to "kiosk" for every fresh install and can only
  // be flipped to "personal" from inside /parent, so a first-time teacher
  // would have no path to ever see a device-mode-gated chooser before their
  // first sign-in. This screen only ever renders while SIGNED OUT — kids on
  // a kiosk device use the already-authenticated WhosPlaying picker and
  // never reach LoginScreen in normal operation, so showing role buttons
  // here doesn't put them on a reading-gated kid path. Choosing a role
  // writes pw_user_role to localStorage before the Google OAuth redirect,
  // so the choice survives the redirect round-trip — this MUST happen at
  // tap time (not on an OAuth "success" callback): the redirect is a full
  // page navigation away and back, so there is no later synchronous point
  // to persist it from.
  //
  // null = step 1 (role choice only); a role = step 2 (that role's auth
  // options, echoed).
  //
  // MA-291: a visitor who arrived via a /claim/:code deep link is a PARENT
  // by definition (the link redeems a per-student claim code) — pre-select
  // the parent role so they land directly on step 2's auth options. This is
  // the ONLY pre-seed: a plain visit still starts at step 1 (no seed from
  // persisted role, per this component's own design note above).
  //
  // MA-1056: the second pre-seed. A visitor who arrived via the website's
  // /teacher/start entry (pendingTeacherStart.ts) lands on step 2 as a
  // teacher. Nothing is written until they act: the role is stored by the
  // sign-in handlers below (Google, Send code, Sign in), never at mount, so
  // an abandoned entry on a shared device writes nothing (MA-1056 RED r1
  // MUST-1). The seed is skipped when this device already holds a family
  // (a local child): that device shows step 1, and "I'm a teacher" still
  // reaches Class setup through the entry. A claim code still wins.
  // "Change role" → "I'm a parent" drops the entry and leaves the teacher
  // URL for the front door.
  const [teacherStartSeed] = useState(
    () => !readPendingClaimCode() && readPendingTeacherStart() !== null && !deviceHasFamilyChildren(),
  );
  const [chosenRole, setChosenRole] = useState<UserRole | null>(() =>
    (readPendingClaimCode() ? "parent" : null) ?? (teacherStartSeed ? "teacher" : null),
  );
  const navigate = useNavigate();

  // MA-289 slice (b): "Continue with email" (magic-link/OTP), flag-gated.
  // Computed once at mount — this screen only ever renders signed-out, so
  // the URL isn't expected to change under it.
  const [emailAuthOn] = useState(() =>
    isEmailAuthEnabled(new URLSearchParams(window.location.search).get("emailAuthPreview")),
  );
  const [emailStep, setEmailStep] = useState<EmailStep>("closed");
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState<string | null>(null);
  // MA-941: the typed code (digits only), its verify state, and the
  // "Send a new code" wait (Supabase refuses a resend inside 60 seconds).
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [codeFailed, setCodeFailed] = useState(false);
  const [resendReady, setResendReady] = useState(false);
  // When "Send a new code" may appear (ms since epoch); set on every real send.
  const [resendAt, setResendAt] = useState(0);
  // The last real send (address + time). "Use a different email" or "Change
  // role" then Send to the SAME address inside 60 s shows the code step again
  // without sending, so the wait cannot be skipped (MA-941 RED S2).
  const lastSendRef = useRef<LastCodeSend>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (emailStep !== "sent") return;
    setResendReady(false);
    const t = setTimeout(() => setResendReady(true), Math.max(0, resendAt - Date.now()));
    return () => clearTimeout(t);
  }, [emailStep, resendAt]);

  // Focus the code field when the code step opens (saves a tap; helps iOS
  // one-time-code autofill).
  useEffect(() => {
    if (emailStep === "sent") codeInputRef.current?.focus();
  }, [emailStep]);

  function recordSend() {
    const at = Date.now();
    lastSendRef.current = { email, at };
    setResendAt(at + EMAIL_CODE_RESEND_AFTER_MS);
  }

  function resetCode() {
    setCode("");
    setCodeFailed(false);
    setVerifying(false);
  }

  function handleRoleSelect(role: UserRole) {
    setChosenRole(role);
    setUserRole(role);
    // MA-1056: a parent choice drops a pending /teacher/start entry, and
    // leaves the teacher URL it forwarded to, so an in-tab sign-in ends on
    // the parent's own front door.
    if (role === "parent" && readPendingTeacherStart() !== null) {
      clearPendingTeacherStart();
      void navigate({ to: "/", replace: true });
    }
  }

  /** MA-1056 RED r1 MUST-1: the chosen role is (re)written when the visitor
   *  signs in, so a pre-seeded role is stored only by a real sign-in action.
   *  Idempotent for a role already written at tap time. */
  function persistChosenRole() {
    if (chosenRole) setUserRole(chosenRole);
  }

  function handleChangeRole() {
    setChosenRole(null);
    setErr(null);
    setEmailStep("closed");
    setEmailErr(null);
    resetCode();
  }

  async function handleLogin() {
    setBusy(true);
    setErr(null);
    persistChosenRole();
    try {
      await signInWithGoogle(); // redirects to Google on success
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function handleSendEmailCode() {
    if (!chosenRole || emailStep === "sending") return;
    setEmailErr(null);
    persistChosenRole();
    if (resendWaitLeftMs(lastSendRef.current, email, Date.now()) > 0) {
      // Same address inside the 60-second wait: back to the code step, no send.
      resetCode();
      setEmailStep("sent");
      return;
    }
    setEmailStep("sending");
    try {
      await signInWithEmailOtp(email, chosenRole);
      resetCode();
      recordSend();
      setEmailStep("sent");
    } catch (e) {
      setEmailErr(e instanceof Error ? e.message : String(e));
      setEmailStep("open");
    }
  }

  function handleUseDifferentEmail() {
    setEmailStep("open");
    setEmailErr(null);
    resetCode();
  }

  // MA-941: Sign in with the typed code. On success the client stores the
  // session and the app's auth listener leaves this screen; any failure
  // (wrong, expired or spent code) shows one calm line.
  async function handleVerifyCode() {
    if (verifying || !isCompleteEmailCode(code)) return;
    persistChosenRole();
    setVerifying(true);
    setCodeFailed(false);
    try {
      await verifyEmailCode(supabase.auth, email, code);
    } catch {
      setCodeFailed(true);
    }
    setVerifying(false);
  }

  async function handleResendCode() {
    if (!chosenRole || verifying) return;
    setResendReady(false);
    setEmailErr(null);
    try {
      await signInWithEmailOtp(email, chosenRole);
      resetCode();
      recordSend();
    } catch (e) {
      setEmailErr(e instanceof Error ? e.message : String(e));
      setResendReady(true);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-6 py-10 text-center font-sans text-foreground">
      {/* wordmark */}
      <p className="m-0 font-serif text-lg font-bold tracking-tight">
        Pencil<span className="text-primary">Wise</span>
      </p>

      {/* hero — Pal on a soft tint ring (same language as the onboarding
          welcome hero) */}
      <div className="relative mt-6 grid place-items-center">
        <div className="absolute h-44 w-44 rounded-full bg-gradient-to-b from-pal-blue/25 to-cream-deep/50" />
        <Pal
          color="blue"
          mood="wave"
          size={150}
          className="relative motion-safe:animate-float-slow"
        />
      </div>

      <h1 className="mx-auto mt-6 max-w-[16ch] font-serif text-[32px] font-bold leading-[1.1] tracking-tight sm:text-[40px]">
        {chosenRole === "teacher" ? TEACHER_SIGN_IN_HEADLINE : "Smart practice that grows with your child."}
      </h1>

      <p className="mx-auto mt-3 max-w-[320px] text-base font-semibold leading-relaxed text-muted-foreground">
        Math, reading, and writing for ages 4–7. Built like a tutor, not a
        worksheet.
      </p>

      {/* MA-289: STEP 1 — role choice ONLY. No auth button exists in the DOM
          until a role is tapped (the "legacy roleless-Google" path is
          removed). MA-57: selecting a role writes pw_user_role before the
          Google OAuth redirect, so the front door (routes/index.tsx) routes
          to the correct surface after sign-in. */}
      {chosenRole === null && (
        <div className="mt-8 flex w-full max-w-[340px] gap-3" role="group" aria-label="I am a">
          <button
            type="button"
            onClick={() => handleRoleSelect("parent")}
            className="flex-1 min-h-[52px] cursor-pointer appearance-none rounded-2xl border-2 border-border bg-background px-4 py-3 text-sm font-bold text-foreground transition-colors hover:border-primary/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            I&apos;m a parent
          </button>
          <button
            type="button"
            onClick={() => handleRoleSelect("teacher")}
            className="flex-1 min-h-[52px] cursor-pointer appearance-none rounded-2xl border-2 border-border bg-background px-4 py-3 text-sm font-bold text-foreground transition-colors hover:border-primary/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            I&apos;m a teacher
          </button>
        </div>
      )}

      {/* MA-289: STEP 2 — this role's sign-in option, echoed, + a one-tap
          way back to change role. */}
      {chosenRole !== null && (
        <div className="mt-8 flex w-full max-w-[340px] flex-col items-center">
          <div className="flex items-center gap-2">
            <p className="m-0 text-sm font-semibold text-muted-foreground">
              Signing in as a {chosenRole}
            </p>
            <button
              type="button"
              onClick={handleChangeRole}
              disabled={busy}
              className="inline-flex min-h-[44px] cursor-pointer appearance-none items-center border-0 bg-transparent px-2 text-sm font-bold text-primary underline underline-offset-2 disabled:cursor-default disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Change role
            </button>
          </div>

          {/* CTA — the confirmed touch-target fix: explicit min-h-[60px]. */}
          <button
            type="button"
            onClick={handleLogin}
            disabled={busy}
            className="mt-4 inline-flex min-h-[60px] w-full cursor-pointer appearance-none items-center justify-center gap-3 rounded-3xl border-0 bg-primary px-6 py-4 text-lg font-extrabold text-primary-foreground shadow-press transition-transform hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-none disabled:cursor-default disabled:opacity-70 disabled:hover:translate-y-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <GoogleMark />
            {busy ? "Opening Google…" : "Continue with Google"}
          </button>

          {/* Designed error state — calm headline, technical detail demoted. */}
          {err && (
            <div
              role="status"
              aria-live="polite"
              className="mt-4 w-full rounded-2xl bg-destructive/10 px-4 py-3 text-left"
            >
              <p className="m-0 text-sm font-bold text-destructive">
                We couldn&apos;t open Google sign-in. Check your connection
                and try again.
              </p>
              <p className="mt-1 text-xs font-semibold text-muted-foreground">
                {err}
              </p>
            </div>
          )}

          {/* MA-289 slice (b): email/magic-link option, flag-gated (see
              src/lib/emailAuthFlag.ts — off by default until the founder
              enables it, docs/ops/FOUNDER-FYI.md). */}
          {emailAuthOn && emailStep === "closed" && (
            <button
              type="button"
              onClick={() => setEmailStep("open")}
              className="mt-3 inline-flex min-h-[44px] w-full cursor-pointer appearance-none items-center justify-center rounded-2xl border-2 border-border bg-background px-6 text-base font-bold text-foreground transition-colors hover:border-primary/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Continue with email
            </button>
          )}

          {emailAuthOn && (emailStep === "open" || emailStep === "sending") && (
            <form
              className="mt-3 flex w-full flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendEmailCode();
              }}
            >
              <label htmlFor="login-email" className="sr-only">
                Email address
              </label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={emailStep === "sending"}
                className="min-h-[44px] w-full appearance-none rounded-2xl border-2 border-border bg-background px-4 text-base font-semibold text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              />
              <button
                type="submit"
                disabled={emailStep === "sending"}
                className="inline-flex min-h-[44px] w-full cursor-pointer appearance-none items-center justify-center rounded-2xl border-0 bg-primary px-6 text-base font-bold text-primary-foreground disabled:cursor-default disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {emailStep === "sending" ? "Sending…" : "Send sign-in code"}
              </button>
              {emailErr && (
                <div
                  role="status"
                  aria-live="polite"
                  className="w-full rounded-2xl bg-destructive/10 px-4 py-3 text-left"
                >
                  <p className="m-0 text-sm font-bold text-destructive">
                    We couldn&apos;t send that code. Check the address and try
                    again.
                  </p>
                  <p className="mt-1 text-xs font-semibold text-muted-foreground">
                    {emailErr}
                  </p>
                </div>
              )}
            </form>
          )}

          {emailAuthOn && emailStep === "sent" && (
            <div className="mt-3 w-full rounded-2xl bg-primary/10 px-4 py-3 text-left">
              <p role="status" aria-live="polite" className="m-0 text-sm font-bold text-foreground">
                Check your email — we sent a sign-in code to {email}.
              </p>
              <form
                className="mt-2 flex w-full flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleVerifyCode();
                }}
              >
                <label htmlFor="login-email-code" className="text-sm font-semibold text-foreground">
                  Code from the email
                </label>
                <input
                  id="login-email-code"
                  ref={codeInputRef}
                  data-testid="email-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  // No maxLength: it would cut a pasted "1234 5678" before the
                  // digits filter runs; emailCodeDigits keeps at most 10 digits.
                  pattern="[0-9]*"
                  value={code}
                  onChange={(e) => {
                    setCode(emailCodeDigits(e.target.value));
                    setCodeFailed(false);
                  }}
                  disabled={verifying}
                  className="min-h-[44px] w-full appearance-none rounded-2xl border-2 border-border bg-background px-4 text-lg font-bold tracking-[0.2em] text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                />
                <button
                  type="submit"
                  disabled={verifying || !isCompleteEmailCode(code)}
                  className="inline-flex min-h-[44px] w-full cursor-pointer appearance-none items-center justify-center rounded-2xl border-0 bg-primary px-6 text-base font-bold text-primary-foreground disabled:cursor-default disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  {verifying ? "Signing in…" : "Sign in"}
                </button>
                {codeFailed && (
                  <p
                    role="alert"
                    data-testid="email-code-error"
                    className="m-0 text-sm font-bold text-destructive"
                  >
                    {EMAIL_CODE_ERROR_TEXT}
                  </p>
                )}
              </form>
              {emailErr && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mt-2 w-full rounded-2xl bg-destructive/10 px-4 py-3 text-left"
                >
                  <p className="m-0 text-sm font-bold text-destructive">
                    We couldn&apos;t send that code. Check the address and try
                    again.
                  </p>
                  <p className="mt-1 text-xs font-semibold text-muted-foreground">
                    {emailErr}
                  </p>
                </div>
              )}
              {resendReady && (
                <button
                  type="button"
                  data-testid="email-code-resend"
                  onClick={handleResendCode}
                  disabled={verifying}
                  className="mt-1 inline-flex min-h-[44px] cursor-pointer appearance-none items-center border-0 bg-transparent px-0 text-sm font-bold text-primary underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  Send a new code
                </button>
              )}
              <button
                type="button"
                onClick={handleUseDifferentEmail}
                className="mt-1 flex min-h-[44px] cursor-pointer appearance-none items-center border-0 bg-transparent px-0 text-sm font-bold text-primary underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                Use a different email
              </button>
            </div>
          )}
        </div>
      )}

      <p className="mt-6 text-sm font-semibold text-muted-foreground">
        Progress syncs across all your devices
      </p>

      {/* MA-09: legal links on the signed-out front door satisfy
          "linked before consent" — a parent can read every policy before
          signing in. Same muted treatment as the line above (passes the
          axe contrast gate). */}
      <nav
        aria-label="Policies"
        className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs font-semibold text-muted-foreground"
      >
        <Link to="/privacy" className="no-underline hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
          Privacy
        </Link>
        <Link to="/terms" className="no-underline hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
          Terms
        </Link>
        <Link to="/coppa-notice" className="no-underline hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
          Children&apos;s Privacy
        </Link>
      </nav>
    </div>
  );
}
